import React from "react";
import { motion } from "framer-motion";
import { Apple, Play, ArrowDown, ScanLine, CheckCircle2, Target, Sparkles } from "lucide-react";
import PhoneMockup from "./PhoneMockup";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-28 lg:pt-36 pb-20 lg:pb-28"
        >
            {/* Ambient blobs */}
            <div
                aria-hidden
                className="blob"
                style={{
                    width: 520,
                    height: 520,
                    top: -120,
                    left: -120,
                    background:
                        "radial-gradient(circle, rgba(22,163,74,0.28), transparent 60%)",
                }}
            />
            <div
                aria-hidden
                className="blob"
                style={{
                    width: 520,
                    height: 520,
                    top: 60,
                    right: -160,
                    background:
                        "radial-gradient(circle, rgba(37,99,235,0.22), transparent 60%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <motion.div
                        className="lg:col-span-7"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                    >
                        <div
                            data-testid="hero-eyebrow"
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#16A34A]/25 bg-[#16A34A]/8 text-[#16A34A] text-xs font-bold tracking-widest uppercase mb-6"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
                            Healthtech · Foodtech
                        </div>

                        <h1
                            data-testid="hero-headline"
                            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-[#0B1C3A] leading-[1.02] tracking-tight"
                        >
                            Descubra o que tem{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10 text-[#16A34A]">
                                    dentro
                                </span>
                                <span
                                    aria-hidden
                                    className="absolute inset-x-0 bottom-1 h-3 lg:h-4 bg-[#16A34A]/15 rounded-sm -z-0"
                                />
                            </span>{" "}
                            antes de colocar no carrinho{" "}
                            <span className="text-[#0B1C3A]/70">
                                (ou na boca)
                            </span>
                            .
                        </h1>

                        <p
                            data-testid="hero-sub"
                            className="mt-6 max-w-2xl text-lg sm:text-xl text-[#475569] leading-relaxed"
                        >
                            O Dentro escaneia produtos, traduz rótulos e mostra,
                            em segundos, se aquele alimento combina com suas
                            restrições, sua saúde e seus objetivos.
                        </p>

                        <a
                            href="#dentria"
                            data-testid="hero-dentria-chip"
                            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0B1C3A]/10 bg-white/60 backdrop-blur text-sm text-[#0B1C3A] hover:border-[#16A34A]/40 hover:bg-white transition-colors"
                        >
                            <Sparkles size={14} className="text-[#16A34A]" />
                            <span>
                                <span className="font-bold">
                                    Powered by DentrIA
                                </span>{" "}
                                — a IA exclusiva do Dentro que traduz rótulos e
                                personaliza a análise para você.
                            </span>
                        </a>

                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <a
                                href="#download"
                                data-testid="hero-cta-ios"
                                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full bg-[#0B1C3A] text-white font-bold shadow-[0_14px_30px_-12px_rgba(11,28,58,0.6)] hover:scale-[1.02] transition-transform"
                            >
                                <Apple size={18} />
                                Baixar para iPhone
                            </a>
                            <a
                                href="#download"
                                data-testid="hero-cta-android"
                                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full bg-[#16A34A] text-white font-bold shadow-[0_14px_30px_-12px_rgba(22,163,74,0.55)] hover:bg-[#15803d] hover:scale-[1.02] transition-all"
                            >
                                <Play size={18} fill="currentColor" />
                                Baixar para Android
                            </a>
                            <a
                                href="#como-funciona"
                                data-testid="hero-cta-howto"
                                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full border border-slate-200 bg-white text-[#0B1C3A] font-bold hover:border-[#0B1C3A] hover:scale-[1.02] transition-all"
                            >
                                <ArrowDown size={18} />
                                Ver como funciona
                            </a>
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-5 text-sm text-[#0B1C3A]/70">
                            <div className="flex items-center gap-2">
                                <ScanLine size={16} className="text-[#16A34A]" />
                                Scan em segundos
                            </div>
                            <div className="hidden sm:block h-3 w-px bg-slate-200" />
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-[#16A34A]" />
                                Alergênicos detectados
                            </div>
                            <div className="hidden sm:block h-3 w-px bg-slate-200" />
                            <div className="flex items-center gap-2">
                                <Target size={16} className="text-[#16A34A]" />
                                Alinhado à sua meta
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        className="lg:col-span-5 flex justify-center lg:justify-end"
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
                    >
                        <div className="relative">
                            {/* Floating badge: Boa escolha */}
                            <div
                                data-testid="hero-badge-boa"
                                className="absolute -top-5 -left-4 sm:-left-6 z-20 rounded-2xl bg-white shadow-xl px-4 py-3 flex items-center gap-3 border border-slate-100 animate-[fadeIn_.6s_ease-out]"
                            >
                                <div className="h-9 w-9 rounded-xl bg-[#16A34A]/12 grid place-items-center">
                                    <CheckCircle2 size={18} className="text-[#16A34A]" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-[#16A34A] font-bold">
                                        Boa escolha
                                    </div>
                                    <div className="text-sm font-semibold text-[#0B1C3A]">
                                        Alinhado ao seu perfil
                                    </div>
                                </div>
                            </div>

                            {/* Floating badge: meta */}
                            <div
                                data-testid="hero-badge-meta"
                                className="absolute -bottom-5 -right-4 sm:-right-6 z-20 rounded-2xl bg-white shadow-xl px-4 py-3 flex items-center gap-3 border border-slate-100 animate-[fadeIn_.6s_ease-out]"
                            >
                                <div className="h-9 w-9 rounded-xl bg-[#2563EB]/12 grid place-items-center">
                                    <Target size={18} className="text-[#2563EB]" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-[#2563EB] font-bold">
                                        Sua meta
                                    </div>
                                    <div className="text-sm font-semibold text-[#0B1C3A]">
                                        Ajuda a manter a saúde
                                    </div>
                                </div>
                            </div>

                            <PhoneMockup data-testid="hero-phone">
                                <HeroFakeScreen />
                            </PhoneMockup>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function HeroFakeScreen() {
    return (
        <div className="w-full h-full bg-white flex flex-col">
            <div className="pt-12 px-5 pb-3">
                <div className="h-1 w-12 rounded-full bg-[#16A34A]" />
                <div className="mt-3 text-[10px] font-bold tracking-[0.2em] uppercase text-[#475569]">
                    Resultado do scan
                </div>
                <h3 className="mt-2 font-display text-[22px] leading-tight font-extrabold text-[#0B1C3A]">
                    🟢 Boa escolha
                </h3>
                <p className="mt-1.5 text-[13px] text-[#475569]">
                    Produto alinhado ao seu perfil.
                </p>
            </div>

            <div className="px-5">
                <div className="rounded-2xl bg-[#F8FAFC] p-4">
                    <div className="flex items-center gap-2 text-[13px] font-semibold text-[#0B1C3A]">
                        <span className="h-6 w-6 rounded-full bg-[#16A34A]/15 grid place-items-center text-[#16A34A]">
                            ✓
                        </span>
                        Poucos ingredientes
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-[13px] font-semibold text-[#0B1C3A]">
                        <span className="h-6 w-6 rounded-full bg-[#16A34A]/15 grid place-items-center text-[#16A34A]">
                            ✓
                        </span>
                        Sem açúcar adicionado
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-[13px] font-semibold text-[#0B1C3A]">
                        <span className="h-6 w-6 rounded-full bg-[#2563EB]/12 grid place-items-center text-[#2563EB]">
                            💪
                        </span>
                        Ajuda na sua meta
                    </div>
                </div>
            </div>

            <div className="mt-auto px-5 pb-6">
                <div className="rounded-2xl bg-[#16A34A] text-white text-center py-3.5 font-bold text-[14px] shadow-[0_10px_20px_-10px_rgba(22,163,74,0.7)]">
                    Ver alternativas melhores
                </div>
            </div>
        </div>
    );
}
